// src/pages/itsm/Changes.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sun, Moon } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, Btn, StatTile, EmptyState, showToast, ToastHost } from '../../components/ui/UI'

const STATUS_CONFIG: Record<string, { label: string; tone: 'success' | 'neutral' | 'warning' | 'danger' | 'info' | 'violet' }> = {
    draft: { label: 'Draft', tone: 'neutral' },
    submitted: { label: 'Submitted', tone: 'info' },
    planning: { label: 'Planning', tone: 'warning' },
    approval: { label: 'Pending Approval', tone: 'violet' },
    approved: { label: 'Approved', tone: 'success' },
    rejected: { label: 'Rejected', tone: 'danger' },
    implementing: { label: 'Implementing', tone: 'warning' },
    review: { label: 'Under Review', tone: 'violet' },
    closed: { label: 'Closed', tone: 'neutral' },
    cancelled: { label: 'Cancelled', tone: 'danger' },
}

const STATUS_FLOW: Record<string, string[]> = {
    draft: ['submitted'],
    submitted: ['planning', 'rejected'],
    planning: ['approval'],
    approval: ['approved', 'rejected'],
    approved: ['implementing'],
    implementing: ['review'],
    review: ['closed'],
}

const RISK_TONE: Record<string, 'success' | 'warning' | 'danger'> = {
    low: 'success', medium: 'warning', high: 'danger'
}

// 'neutral' has no semantic *Bg/*Border pair in T, so fall back to the
// surface/border tokens Pill uses for jr-pill-neutral.
const toneColor = (tone: string) => tone === 'neutral' ? T.text2 : (T as any)[tone]
const toneBg = (tone: string) => tone === 'neutral' ? T.surface2 : (T as any)[tone + 'Bg']
const toneBorder = (tone: string) => tone === 'neutral' ? T.border : (T as any)[tone + 'Border']

export default function Changes() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [changes, setChanges] = useState<any[]>([])
    const [agents, setAgents] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [showAdd, setShowAdd] = useState(false)
    const [selected, setSelected] = useState<any>(null)
    const [comment, setComment] = useState('')
    const [sending, setSending] = useState(false)
    const [comments, setComments] = useState<any[]>([])
    const [statusFilter, setStatusFilter] = useState('all')

    const [form, setForm] = useState({
        title: '', description: '', type: 'normal', priority: 'medium',
        risk: 'low', impact: 'low', requested_by: '', assigned_to: '',
        planned_start: '', planned_end: '', rollback_plan: '',
        implementation_plan: '', test_plan: '',
    })

    useEffect(() => { fetchAll() }, [statusFilter])

    const fetchAll = async () => {
        let query = supabase
            .from('itsm_changes')
            .select('*, itsm_agents!requested_by(name), itsm_agents!assigned_to(name)')
            .order('created_at', { ascending: false })

        if (statusFilter !== 'all') query = query.eq('status', statusFilter)

        const [{ data: c }, { data: a }] = await Promise.all([
            query,
            supabase.from('itsm_agents').select('id, name').eq('is_active', true),
        ])
        setChanges(c || [])
        setAgents(a || [])
        setLoading(false)
    }

    const fetchComments = async (changeId: number) => {
        const { data } = await supabase
            .from('itsm_change_comments')
            .select('*')
            .eq('change_id', changeId)
            .order('created_at')
        setComments(data || [])
    }

    const saveChange = async () => {
        await supabase.from('itsm_changes').insert([{
            ...form,
            requested_by: form.requested_by || null,
            assigned_to: form.assigned_to || null,
            planned_start: form.planned_start || null,
            planned_end: form.planned_end || null,
        }])
        setForm({ title: '', description: '', type: 'normal', priority: 'medium', risk: 'low', impact: 'low', requested_by: '', assigned_to: '', planned_start: '', planned_end: '', rollback_plan: '', implementation_plan: '', test_plan: '' })
        setShowAdd(false)
        showToast('Change request submitted', 'success')
        fetchAll()
    }

    const changeStatus = async (changeId: number, newStatus: string) => {
        await supabase.from('itsm_changes').update({ status: newStatus }).eq('id', changeId)
        fetchAll()
        if (selected) setSelected({ ...selected, status: newStatus })
        showToast(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`, 'success')
    }

    const sendComment = async () => {
        if (!comment.trim() || !selected) return
        setSending(true)
        await supabase.from('itsm_change_comments').insert([{
            change_id: selected.id,
            author_name: 'Agent',
            message: comment,
        }])
        setComment('')
        await fetchComments(selected.id)
        setSending(false)
    }

    const openDetail = async (change: any) => {
        setSelected(change)
        await fetchComments(change.id)
    }

    const s = styles

    if (loading) return (
        <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>
    )

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>

            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Change Management</h1>
                    <p style={s.subtitle}>{changes.length} change requests</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <Btn variant="primary" style={undefined} onClick={() => setShowAdd(true)}>+ New Change</Btn>
                </div>
            </div>

            {/* Stats */}
            <Card style={{ padding: 0, display: 'flex', overflow: 'hidden', marginBottom: 20 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Total" value={changes.length} tone="info" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Pending Approval" value={changes.filter(c => c.status === 'approval').length} tone="violet" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Implementing" value={changes.filter(c => c.status === 'implementing').length} tone="warning" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <StatTile label="Closed" value={changes.filter(c => c.status === 'closed').length} tone="success" />
                </div>
            </Card>

            {/* Filter */}
            <div style={{ marginBottom: 16 }}>
                <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All Status</option>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: 20 }}>
                {/* List */}
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                {['#', 'Title', 'Type', 'Risk', 'Impact', 'Status', 'Planned Start', 'Assigned'].map(h => (
                                    <th key={h} style={s.th}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {changes.map(c => {
                                const status = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft
                                return (
                                    <tr key={c.id} style={{ ...s.tr, background: selected?.id === c.id ? T.surface2 : 'transparent' }}
                                        onClick={() => openDetail(c)}>
                                        <td style={s.td}><span className="jr-mono" style={{ fontSize: 11, color: T.text3, fontWeight: 700 }}>{c.change_number}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.title}</span></td>
                                        <td style={s.td}><Pill tone="neutral">{c.type}</Pill></td>
                                        <td style={s.td}><span style={{ fontSize: 11, fontWeight: 700, color: T[RISK_TONE[c.risk]] }}>{c.risk?.toUpperCase()}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 11, fontWeight: 700, color: T[RISK_TONE[c.impact]] }}>{c.impact?.toUpperCase()}</span></td>
                                        <td style={s.td}><Pill tone={status.tone}>{status.label}</Pill></td>
                                        <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{c.planned_start ? new Date(c.planned_start).toLocaleDateString('en-GB') : '—'}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{c.itsm_agents?.name || '—'}</span></td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    {changes.length === 0 && <EmptyState icon="🔄" title="No changes found" />}
                </Card>

                {/* Detail Panel */}
                {selected && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <Card>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                                <div>
                                    <span className="jr-mono" style={{ fontSize: 11, color: T.text3, fontWeight: 700 }}>{selected.change_number}</span>
                                    <p style={{ fontSize: 15, fontWeight: 700, color: T.text, marginTop: 4 }}>{selected.title}</p>
                                </div>
                                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: T.text3 }}>✕</button>
                            </div>

                            {/* Status Actions */}
                            {STATUS_FLOW[selected.status]?.length > 0 && (
                                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' as const }}>
                                    {STATUS_FLOW[selected.status].map(next => (
                                        <button key={next}
                                            style={{ height: 30, padding: '0 12px', background: toneBg(STATUS_CONFIG[next]?.tone || 'info'), color: toneColor(STATUS_CONFIG[next]?.tone || 'info'), border: `1px solid ${toneBorder(STATUS_CONFIG[next]?.tone || 'info')}`, borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                            onClick={() => changeStatus(selected.id, next)}>
                                            → {STATUS_CONFIG[next]?.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <table style={{ width: '100%', marginBottom: 14 }}>
                                <tbody>
                                    {[
                                        { label: 'Status', value: STATUS_CONFIG[selected.status]?.label },
                                        { label: 'Type', value: selected.type },
                                        { label: 'Priority', value: selected.priority },
                                        { label: 'Risk', value: selected.risk },
                                        { label: 'Impact', value: selected.impact },
                                        { label: 'Planned Start', value: selected.planned_start ? new Date(selected.planned_start).toLocaleDateString('en-GB') : '—' },
                                        { label: 'Planned End', value: selected.planned_end ? new Date(selected.planned_end).toLocaleDateString('en-GB') : '—' },
                                    ].map(r => (
                                        <tr key={r.label}>
                                            <td style={{ fontSize: 11, color: T.text3, padding: '4px 0', paddingRight: 12, fontWeight: 600, textTransform: 'capitalize' }}>{r.label}</td>
                                            <td style={{ fontSize: 12, color: T.text, fontWeight: 500, padding: '4px 0', textTransform: 'capitalize' }}>{r.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {selected.description && (
                                <div style={{ padding: '10px 12px', background: T.surface2, borderRadius: 8, marginBottom: 12 }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', marginBottom: 4 }}>Description</p>
                                    <p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{selected.description}</p>
                                </div>
                            )}

                            {selected.implementation_plan && (
                                <div style={{ padding: '10px 12px', background: T.successBg, borderRadius: 8, marginBottom: 8, border: `1px solid ${T.successBorder}` }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: T.success, textTransform: 'uppercase', marginBottom: 4 }}>Implementation Plan</p>
                                    <p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{selected.implementation_plan}</p>
                                </div>
                            )}

                            {selected.rollback_plan && (
                                <div style={{ padding: '10px 12px', background: T.dangerBg, borderRadius: 8, border: `1px solid ${T.dangerBorder}` }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: T.danger, textTransform: 'uppercase', marginBottom: 4 }}>Rollback Plan</p>
                                    <p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{selected.rollback_plan}</p>
                                </div>
                            )}
                        </Card>

                        {/* Comments */}
                        <Card>
                            <p style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Comments</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                                {comments.map(c => (
                                    <div key={c.id} style={{ padding: '8px 12px', background: T.surface2, borderLeft: `3px solid ${T.border}`, borderRadius: '0 6px 6px 0' }}>
                                        <p style={{ fontSize: 11, color: T.text3, marginBottom: 3, fontWeight: 600 }}>{c.author_name} · {new Date(c.created_at).toLocaleDateString('en-GB')}</p>
                                        <p style={{ fontSize: 13, color: T.text2 }}>{c.message}</p>
                                    </div>
                                ))}
                                {comments.length === 0 && <p style={{ fontSize: 12, color: T.text3 }}>No comments yet</p>}
                            </div>
                            <textarea style={s.textarea}
                                placeholder="Add a comment..." value={comment} onChange={e => setComment(e.target.value)} />
                            <Btn variant="primary" style={undefined}
                                onClick={sendComment} disabled={sending || !comment.trim()}>
                                {sending ? 'Sending...' : 'Send'}
                            </Btn>
                        </Card>
                    </div>
                )}
            </div>

            {/* Add Modal */}
            {showAdd && (
                <div style={s.overlay}>
                    <div style={{ ...s.modal, maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={s.modalTitle}>New Change Request</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={{ ...s.field, gridColumn: 'span 2' }}>
                                <label style={s.label}>Title *</label>
                                <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Update firewall rules..." />
                            </div>
                            <div style={{ ...s.field, gridColumn: 'span 2' }}>
                                <label style={s.label}>Description</label>
                                <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Type</label>
                                <select style={s.input} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                    <option value="normal">Normal</option>
                                    <option value="emergency">Emergency</option>
                                    <option value="standard">Standard</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Priority</label>
                                <select style={s.input} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Risk</label>
                                <select style={s.input} value={form.risk} onChange={e => setForm({ ...form, risk: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Impact</label>
                                <select style={s.input} value={form.impact} onChange={e => setForm({ ...form, impact: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Requested By</label>
                                <select style={s.input} value={form.requested_by} onChange={e => setForm({ ...form, requested_by: e.target.value })}>
                                    <option value="">Select agent...</option>
                                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Assigned To</label>
                                <select style={s.input} value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                                    <option value="">Select agent...</option>
                                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Planned Start</label>
                                <input type="datetime-local" style={s.input} value={form.planned_start} onChange={e => setForm({ ...form, planned_start: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Planned End</label>
                                <input type="datetime-local" style={s.input} value={form.planned_end} onChange={e => setForm({ ...form, planned_end: e.target.value })} />
                            </div>
                            <div style={{ ...s.field, gridColumn: 'span 2' }}>
                                <label style={s.label}>Implementation Plan</label>
                                <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={form.implementation_plan} onChange={e => setForm({ ...form, implementation_plan: e.target.value })} />
                            </div>
                            <div style={{ ...s.field, gridColumn: 'span 2' }}>
                                <label style={s.label}>Rollback Plan</label>
                                <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={form.rollback_plan} onChange={e => setForm({ ...form, rollback_plan: e.target.value })} />
                            </div>
                            <div style={{ ...s.field, gridColumn: 'span 2' }}>
                                <label style={s.label}>Test Plan</label>
                                <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={form.test_plan} onChange={e => setForm({ ...form, test_plan: e.target.value })} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                            <Btn variant="ghost" style={{ flex: 1 }} onClick={() => setShowAdd(false)}>Cancel</Btn>
                            <Btn variant="primary" style={{ flex: 1 }} onClick={saveChange}>Submit Change</Btn>
                        </div>
                    </div>
                </div>
            )}

            <ToastHost />
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1300, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    back: { background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    subtitle: { fontSize: 13, color: T.text2 },
    select: { height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', background: T.surface, color: T.text2 },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `1px solid ${T.border}`, verticalAlign: 'middle' as const },
    tr: { borderBottom: `1px solid ${T.border}`, cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%', border: `1px solid ${T.border}` },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
    textarea: { width: '100%', height: 70, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, resize: 'none', outline: 'none', marginBottom: 8, background: T.surface, color: T.text },
}
